import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Avatar } from '@mui/material';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import StarIcon from '@mui/icons-material/Star';
import { landingColors } from '../../../theme/landingTheme';

const testimonials = [
  {
    quote:
      "Nexera transformed how we manage our 5 electronics stores. The real-time inventory sync alone saved us from countless stockout situations. It's like having a command center for our entire operation.",
    name: 'Ahmed Benali',
    title: 'Owner, TechZone Electronics',
    avatar: 'AB',
    rating: 5,
    color: '#6366F1',
  },
  {
    quote:
      "The POS system is incredibly fast. Our checkout time dropped by 40%. Plus, the B2B portal helped us manage our wholesale partners without the spreadsheet chaos we had before.",
    name: 'Fatima Khelifi',
    title: 'Manager, BookWorm Bookstore',
    avatar: 'FK',
    rating: 5,
    color: '#10B981',
  },
  {
    quote:
      "We started with the free tier to test it out. Within a month, we upgraded to Pro. The analytics dashboards give us insights we never had before. Highly recommend for any retail business.",
    name: 'Youcef Hamdi',
    title: 'Founder, QuickMart',
    avatar: 'YH',
    rating: 5,
    color: '#F59E0B',
  },
];

export const TestimonialsSection: React.FC = () => {
  return (
    <Box
      id="testimonials"
      component="section"
      sx={{
        py: { xs: 10, md: 16 },
        position: 'relative',
        backgroundColor: landingColors.surface,
        overflow: 'hidden',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '150%',
          height: '150%',
          background: `radial-gradient(ellipse at center, rgba(99, 102, 241, 0.03) 0%, transparent 50%)`,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: 640,
            mx: 'auto',
            mb: { xs: 6, md: 10 },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: 'inline-block',
              mb: 2,
              px: 2,
              py: 0.5,
              backgroundColor: landingColors.primaryMuted,
              borderRadius: '6px',
            }}
          >
            Testimonials
          </Typography>
          <Typography variant="h2" sx={{ mb: 3 }}>
            Loved by Store Owners
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', md: '1.125rem' },
              color: landingColors.textSecondary,
            }}
          >
            Don't just take our word for it. Here's what our customers have to say
            about Nexera.
          </Typography>
        </Box>

        {/* Testimonials Grid */}
        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: `1px solid ${landingColors.border}`,
                  borderRadius: '20px',
                  position: 'relative',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 24px 48px rgba(15, 23, 42, 0.1)`,
                    borderColor: `${testimonial.color}40`,
                    '& .quote-icon': {
                      transform: 'scale(1.1)',
                      color: testimonial.color,
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  {/* Quote Icon */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 3,
                    }}
                  >
                    <FormatQuoteIcon
                      className="quote-icon"
                      sx={{
                        fontSize: 40,
                        color: landingColors.border,
                        transform: 'scaleX(-1)',
                        transition: 'all 0.3s ease',
                      }}
                    />
                    {/* Rating */}
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <StarIcon
                          key={i}
                          sx={{
                            fontSize: 18,
                            color: '#F59E0B',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Quote */}
                  <Typography
                    variant="body1"
                    sx={{
                      color: landingColors.textPrimary,
                      lineHeight: 1.8,
                      mb: 4,
                      fontStyle: 'italic',
                      position: 'relative',
                    }}
                  >
                    "{testimonial.quote}"
                  </Typography>

                  {/* Author */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      pt: 3,
                      borderTop: `1px solid ${landingColors.border}`,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        background: `linear-gradient(135deg, ${testimonial.color} 0%, ${testimonial.color}CC 100%)`,
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      {testimonial.avatar}
                    </Avatar>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: landingColors.textPrimary,
                          fontSize: '0.9375rem',
                        }}
                      >
                        {testimonial.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: landingColors.textMuted,
                          fontSize: '0.8125rem',
                        }}
                      >
                        {testimonial.title}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Stats Banner */}
        <Box
          sx={{
            mt: 10,
            p: { xs: 4, md: 6 },
            borderRadius: '24px',
            background: `linear-gradient(135deg, ${landingColors.primary} 0%, ${landingColors.secondary} 100%)`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Pattern overlay */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              opacity: 0.5,
              pointerEvents: 'none',
            }}
          />

          <Grid container spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
            {[
              { value: '500+', label: 'Stores Worldwide' },
              { value: '2M+', label: 'Products Managed' },
              { value: '99.9%', label: 'Uptime SLA' },
              { value: '4.9/5', label: 'Customer Rating' },
            ].map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    sx={{
                      fontFamily: '"Cabinet Grotesk", sans-serif',
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      fontWeight: 800,
                      color: '#fff',
                      lineHeight: 1,
                      mb: 0.5,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;
